import React, { useState, useEffect } from 'react'
import { X, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export default function ParishApplicationModal({ open, onClose }) {
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({
    parishName: '',
    diocese: '',
    city: '',
    state: '',
    contactName: '',
    contactEmail: '',
    phone: '',
    notes: '',
  })

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSubmitted(false)
      setForm({ parishName: '', diocese: '', city: '', state: '', contactName: '', contactEmail: '', phone: '', notes: '' })
    }
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white rounded shadow-xl border border-surface-border max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-start justify-between px-8 pt-8 pb-6 border-b border-surface-border">
          <div>
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-1">
              Parish Access
            </p>
            <h2 className="text-2xl font-bold font-display text-charcoal">
              Apply for Access
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-charcoal-muted hover:text-charcoal transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-gold/50 rounded p-1 -mt-1 -mr-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          /* Success State */
          <div className="px-8 py-12 text-center">
            <div className="w-14 h-14 border-2 border-gold rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-7 h-7 text-gold" />
            </div>
            <h3 className="text-xl font-bold font-display text-charcoal mb-3">
              Application Received
            </h3>
            <p className="text-sm text-charcoal-muted max-w-sm mx-auto mb-8">
              Thank you for applying. Our team will review your information and reach out to{' '}
              <span className="text-charcoal font-medium">{form.contactEmail}</span> shortly.
            </p>
            <Button onClick={onClose} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
              Done
            </Button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
            <p className="text-sm text-charcoal-muted">
              Tell us about your parish and we'll get you set up on the platform.
            </p>

            {/* Parish Name */}
            <div className="space-y-1.5">
              <Label htmlFor="parishName">Parish Name</Label>
              <Input
                id="parishName"
                name="parishName"
                placeholder="St. Michael's Catholic Church"
                value={form.parishName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Diocese */}
            <div className="space-y-1.5">
              <Label htmlFor="diocese">
                Diocese <span className="text-charcoal-faint normal-case tracking-normal font-normal">(optional)</span>
              </Label>
              <Input
                id="diocese"
                name="diocese"
                placeholder="Archdiocese of Chicago"
                value={form.diocese}
                onChange={handleChange}
              />
            </div>

            {/* City + State */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  placeholder="Chicago"
                  value={form.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  name="state"
                  placeholder="IL"
                  value={form.state}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Contact Name */}
            <div className="space-y-1.5">
              <Label htmlFor="contactName">Contact Name</Label>
              <Input
                id="contactName"
                name="contactName"
                placeholder="Fr. John Smith"
                value={form.contactName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Contact Email */}
            <div className="space-y-1.5">
              <Label htmlFor="contactEmail">Contact Email</Label>
              <Input
                id="contactEmail"
                name="contactEmail"
                type="email"
                placeholder="pastor@stmichaels.org"
                value={form.contactEmail}
                onChange={handleChange}
                required
              />
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
              <Label htmlFor="phone">
                Phone <span className="text-charcoal-faint normal-case tracking-normal font-normal">(optional)</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="(312) 555-0100"
                value={form.phone}
                onChange={handleChange}
              />
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="notes">
                Additional Notes <span className="text-charcoal-faint normal-case tracking-normal font-normal">(optional)</span>
              </Label>
              <textarea
                id="notes"
                name="notes"
                rows={3}
                placeholder="Anything you'd like us to know about your parish..."
                value={form.notes}
                onChange={handleChange}
                className={cn(
                  "w-full rounded border border-surface-border bg-white px-3 py-2 text-sm text-charcoal placeholder:text-charcoal-faint focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold resize-none transition-colors duration-150"
                )}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2 pb-2">
              <Button
                type="submit"
                className="flex-1 bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold"
              >
                Submit Application
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
